import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  Cell,
  XAxis,
  YAxis,
} from "recharts";
import { useMasterData } from "../../../hooks/useMasterData";
import PerformanceTable from "./PerformanceTable";
import {
  CreatePerformancePayload,
  PerformanceDashboardResponse,
  PerformanceMatrixRow,
  PerformanceExamResult,
  PerformanceFilters,
  fetchPerformanceDashboard,
  createOrUpdatePerformance,
} from "./performanceApi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { studentsApi } from "../students/api";

interface AcademicSession {
  id: string;
  name: string;
  isActive: boolean;
}

interface StudentOption {
  id: string;
  name: string;
  admissionNumber: string;
}

const GRADE_COLORS: Record<string, string> = {
  "A+": "#42a5f5",
  A: "#66bb6a",
  "B+": "#ffca28",
  B: "#ffa726",
  C: "#ef5350",
  D: "#8d6e63",
};

const StudentPerformancePage: React.FC = () => {
  const navigate = useNavigate();
  const { masters, loading: mastersLoading, fetchMasters, fetchSections } =
    useMasterData();

  const [sessions, setSessions] = useState<AcademicSession[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);

  const [filters, setFilters] = useState<PerformanceFilters>({});
  const [data, setData] = useState<PerformanceDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get<AcademicSession[]>(
        "/system-settings/academic-sessions",
      );
      const allSessions = res.data || [];
      setSessions(allSessions);
      const active = allSessions.find((s) => s.isActive);
      if (active && !filters.academicSessionId) {
        setFilters((prev) => ({
          ...prev,
          academicSessionId: active.id,
        }));
      }
    } catch {
      setSessions([]);
    }
  }, [filters.academicSessionId]);

  useEffect(() => {
    fetchMasters();
    loadSessions();
  }, [fetchMasters, loadSessions]);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then((secs) => setSections(secs || []));
    } else {
      setSections([]);
    }
  }, [filters.classId, fetchSections]);

  const loadStudents = useCallback(async () => {
    if (!filters.classId || !filters.sectionId) {
      setStudents([]);
      return;
    }
    try {
      const res = await studentsApi.getAll({
        page: 1,
        limit: 100,
        classId: filters.classId,
        sectionId: filters.sectionId,
      });
      const list = res.data || [];
      setStudents(
        list.map((s: any) => ({
          id: s.id,
          name: `${s.firstName} ${s.lastName}`,
          admissionNumber: s.admissionNumber,
        })),
      );
    } catch {
      setStudents([]);
    }
  }, [filters.classId, filters.sectionId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const handleFilterChange = (
    key: keyof PerformanceFilters,
    value: string,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const loadDashboard = useCallback(
    async (currentFilters: PerformanceFilters) => {
      setLoading(true);
      try {
        const response = await fetchPerformanceDashboard(currentFilters);
        setData(response);
        setError(null);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Failed to load performance");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (mastersLoading) return;
    if (!filters.academicSessionId) return;
    loadDashboard(filters);
  }, [mastersLoading, filters, loadDashboard]);

  const handleChangeMarks = async ({
    subjectId,
    examId,
    marksObtained,
    maxMarks,
  }: {
    subjectId: string;
    examId: string;
    marksObtained: number;
    maxMarks: number;
  }) => {
    if (!data || !filters.studentId || !filters.academicSessionId) return;

    const studentSessionRecordId = (data as any).sessionRecordId || undefined;

    const payload: CreatePerformancePayload = {
      studentId: filters.studentId,
      studentSessionRecordId,
      academicSessionId: filters.academicSessionId,
      classId:
        filters.classId || ((data.class as any)?.id as string | undefined),
      sectionId:
        filters.sectionId || ((data.section as any)?.id as string | undefined),
      examId,
      subjectId,
      marksObtained,
      maxMarks,
    };

    setSaving(true);
    try {
      await createOrUpdatePerformance(payload);
      await loadDashboard(filters);
    } finally {
      setSaving(false);
    }
  };

  const subjectWiseData = useMemo(() => {
    if (!data) return [];
    return data.performanceMatrix.map((row: PerformanceMatrixRow) => {
      let totalObtained = 0;
      let totalMax = 0;
      row.examResults.forEach((r: PerformanceExamResult) => {
        totalObtained += r.marksObtained;
        totalMax += r.maxMarks;
      });
      const percentage = totalMax > 0 ? (totalObtained / totalMax) * 100 : 0;
      return {
        subject: row.subjectName,
        percentage,
      };
    });
  }, [data]);

  const examWiseData = useMemo(() => {
    if (!data) return [];
    const map: Record<string, { name: string; total: number; count: number }> =
      {};
    data.performanceMatrix.forEach((row) => {
      row.examResults.forEach((r) => {
        if (!map[r.examId]) {
          map[r.examId] = { name: r.examName, total: 0, count: 0 };
        }
        const percentage =
          r.maxMarks > 0 ? (r.marksObtained / r.maxMarks) * 100 : 0;
        map[r.examId].total += percentage;
        map[r.examId].count += 1;
      });
    });
    return Object.keys(map).map((id) => {
      const entry = map[id];
      return {
        exam: entry.name,
        average: entry.count > 0 ? entry.total / entry.count : 0,
      };
    });
  }, [data]);

  const gradeDistribution = useMemo(() => {
    if (!data || !data.performanceMatrix.length) return [];
    const counts: Record<string, number> = {};
    data.performanceMatrix.forEach((row) => {
      row.examResults.forEach((r) => {
        if (!r.grade) return;
        counts[r.grade] = (counts[r.grade] || 0) + 1;
      });
    });
    return Object.keys(counts).map((grade) => ({
      grade,
      value: counts[grade],
    }));
  }, [data]);

  const handleOpenStudentProfile = () => {
    if (!filters.studentId) return;
    navigate(`/scholar-register/students/${filters.studentId}`);
  };

  const summary = data?.summary;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography variant="h4">Student Performance</Typography>
        {filters.studentId && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenStudentProfile}
          >
            View Student Profile
          </Button>
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, borderRadius: 2, display: "flex", gap: 2, flexWrap: "wrap" }}
      >
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="textSecondary">
            Academic Session
          </Typography>
          <Select
            fullWidth
            size="small"
            value={filters.academicSessionId || ""}
            onChange={(e) =>
              handleFilterChange("academicSessionId", e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">
              <em>All Sessions</em>
            </MenuItem>
            {sessions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
                {s.isActive ? " (Current)" : ""}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="textSecondary">
            Class
          </Typography>
          <Select
            fullWidth
            size="small"
            value={filters.classId || ""}
            onChange={(e) =>
              handleFilterChange("classId", e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">
              <em>All Classes</em>
            </MenuItem>
            {masters.classes.map((c: any) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="textSecondary">
            Section
          </Typography>
          <Select
            fullWidth
            size="small"
            value={filters.sectionId || ""}
            onChange={(e) =>
              handleFilterChange("sectionId", e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">
              <em>All Sections</em>
            </MenuItem>
            {sections.map((s: any) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ minWidth: 240 }}>
          <Typography variant="caption" color="textSecondary">
            Student
          </Typography>
          <Select
            fullWidth
            size="small"
            value={filters.studentId || ""}
            onChange={(e) =>
              handleFilterChange("studentId", e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">
              <em>All Students</em>
            </MenuItem>
            {students.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.admissionNumber} - {s.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ minWidth: 220 }}>
          <Typography variant="caption" color="textSecondary">
            Exam
          </Typography>
          <Select
            fullWidth
            size="small"
            value={filters.examId || ""}
            onChange={(e) =>
              handleFilterChange("examId", e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">
              <em>All Exams</em>
            </MenuItem>
            {data?.exams.map((e) => (
              <MenuItem key={e.id} value={e.id}>
                {e.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => loadDashboard(filters)}
          >
            Refresh
          </Button>
          <Button
            variant={editing ? "outlined" : "contained"}
            color="secondary"
            onClick={() => setEditing((prev) => !prev)}
          >
            {editing ? "Stop Entry" : "Performance Entry"}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: "error.main" }}
        >
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              {loading && !summary ? (
                <Skeleton variant="rectangular" height={120} />
              ) : summary ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Performance Summary
                  </Typography>
                  <Typography variant="subtitle1">
                    Overall Percentage:{" "}
                    {summary.overallPercentage.toFixed(2)}%
                  </Typography>
                  <Typography variant="subtitle1">
                    Overall Grade: {summary.overallGrade}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total: {summary.totalMarksObtained}/
                    {summary.totalMaxMarks}
                  </Typography>
                </>
              ) : (
                <Typography color="textSecondary">
                  No summary available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          {loading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <PerformanceTable
              data={data}
              editing={editing}
              onChangeMarks={handleChangeMarks}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Subject Wise Performance
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : subjectWiseData.length ? (
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={subjectWiseData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="subject" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="percentage" fill="#42a5f5" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography color="textSecondary">
                  No data to display.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Exam Wise Average
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : examWiseData.length ? (
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={examWiseData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="exam" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="average"
                        stroke="#66bb6a"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography color="textSecondary">
                  No data to display.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Grade Distribution
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : gradeDistribution.length ? (
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        dataKey="value"
                        data={gradeDistribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        label
                      >
                        {gradeDistribution.map((entry, index) => (
                          <Cell
                            key={entry.grade}
                            fill={
                              GRADE_COLORS[entry.grade] ||
                              Object.values(GRADE_COLORS)[
                                index % Object.values(GRADE_COLORS).length
                              ]
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography color="textSecondary">
                  No grade data to display.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default StudentPerformancePage;
